import { all, call, put, takeEvery, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';

import { uploadFailed, garmentPlacementOutsideBounds, garmentPlacementFailed } from 'src/components/alert/slice';
import { getRegionSetting } from 'src/selectors';
import { toSafeUploadName, uploadImageAndGetUrl } from 'src/util/upload';
import { cleanTexture } from 'src/services/sceneClient';
import { logError } from 'src/loggingManager';
import {
    getCylinderWarpById,
    getSceneSize,
    getSceneDefiningBackgroundId,
    getBackgroundById,
    getDocumentById,
    getAllDocuments,
} from 'src/selectors/layers';
import { getTransformsById } from 'src/selectors/transforms';
import { getDecorationSpecs, getSurfaceByPage } from 'src/selectors/pages/editor';
import { PERSPECTIVE_TRANSFORM, TUBE_TRANSFORM, PLACEMENT_TRANSFORM } from 'src/models/transforms';
import { CYLINDER_WARP, GARMENT_WARP } from 'src/models/documentWarps';
import {
    computeAutoPosition,
    DEFAULT_SCALE_AXIS,
    type AutoPositionFailureReason,
    type ScaleAxis,
} from 'src/util/autoPosition';
import { defaultMarkers } from 'src/pages/editor/components/canvas/garmentMarkers/defaultMarkers';
import type { GarmentPlacementInputType } from 'src/types/decorationSpecs';
import {
    removeMask,
    uploadMask,
    maskUploaded,
    setWarp,
    changePage,
    replaceTransforms,
    updateCylinder,
    toggleFullWrap,
    updatePrecisionWrap,
    rotateCylinder,
    textureUploaded,
    removeTexture,
    uploadTexture,
    recomputeGarmentPlacement,
} from './slice';
import { requestGarmentPlacementRecompute } from './garmentPlacement/actions';
import { parseCylinderWarp, processTubeWarp } from 'src/util/cylinderWarp/tubeWarpTransform';
import { retrieveSurfacesSuccess } from 'src/pages/editor/components/surfaces/slice';
import { retrieveDecorationSpecsSettled } from 'src/pages/editor/components/decorationSpecs/slice';

type CylinderWarpParams = {
    cylinderTop: {
        center: { x: number; y: number };
        radiusX: number;
        radiusY: number;
        rotationDegrees: number;
    };
    cylinderBottom: {
        center: { x: number; y: number };
        radiusX: number;
        radiusY: number;
        rotationDegrees: number;
    };
    decorationAreaDimensions: { width: number; height: number };
    productDiameter: number;
    decorationExtentDegreesAlpha: number;
    decorationExtentDegreesBeta: number;
    isDecorationRotated: boolean;
    imageDimensions: { width: number; height: number };
};

function createCylinderWarpParameters(width: number, height: number): CylinderWarpParams {
    const middleX = width / 2;
    const topY = height * 0.3;
    const bottomY = height * 0.7;
    const ellipseWidth = width * 0.2;
    const ellipseHeight = height * 0.05;

    const productDiameter = width * 0.5;
    const decorationAreaSize = (productDiameter * Math.PI) / 4;
    return {
        cylinderTop: {
            center: { x: middleX, y: topY },
            radiusX: ellipseWidth,
            radiusY: ellipseHeight,
            rotationDegrees: 0.0,
        },
        cylinderBottom: {
            center: { x: middleX, y: bottomY },
            radiusX: ellipseWidth,
            radiusY: ellipseHeight,
            rotationDegrees: 0.0,
        },
        decorationAreaDimensions: {
            width: decorationAreaSize,
            height: decorationAreaSize,
        },
        productDiameter,
        decorationExtentDegreesAlpha: 225,
        decorationExtentDegreesBeta: 90,
        isDecorationRotated: false,
        imageDimensions: { width, height },
    };
}

function* uploadAndAddMask({ payload }: PayloadAction<{ file: File; id: string }>): Generator<any, void, any> {
    const { file, id } = payload;
    const region = yield* select(getRegionSetting);
    const sceneSize = yield* select(getSceneSize);
    try {
        const url = (yield* call(uploadImageAndGetUrl as any, file, region, sceneSize)) as string;
        yield* put(maskUploaded({ url, id, fileName: toSafeUploadName(file.name) }));
    } catch (error: any) {
        logError(
            `unable to upload mask, there was an error while uploading/updating: ${error} ${file.name} ${file.size} ${file.type}`,
        );
        yield* put(uploadFailed(`Mask: ${error}`));
        yield* put(removeMask(id));
    }
}

function* uploadAndAddTexture({ payload }: PayloadAction<{ file: File; id: string }>): Generator<any, void, any> {
    const { file, id } = payload;
    const region = yield* select(getRegionSetting);
    try {
        const cleanedTexture = yield* call(cleanTexture, { texture: file });
        const url = yield* call(uploadImageAndGetUrl, cleanedTexture, region);
        yield* put(textureUploaded({ url, id, fileName: file.name }));
    } catch (error: any) {
        logError(
            `unable to upload texture, there was an error while uploading/updating: ${error} ${file.name} ${file.size} ${file.type}`,
        );
        yield* put(uploadFailed(`Texture: ${error}`));
        yield* put(removeTexture(id));
    }
}

function* uploadCylinderWarp(id: string): Generator<any, void, any> {
    const warp = yield* select(getCylinderWarpById, id);
    const documentTransforms = yield* select(getTransformsById, id);
    const perspectiveId = documentTransforms.find((transform: any) => transform.type === PERSPECTIVE_TRANSFORM)!.id;
    const cylinderId = documentTransforms.find((transform: any) => transform.type === TUBE_TRANSFORM)!.id;
    const placement = documentTransforms.find((transform: any) => transform.type === PLACEMENT_TRANSFORM);
    const placementId = placement ? placement.id : null;

    // TODO: Review this line of code
    const response: any = (parseCylinderWarp as any)(
        (processTubeWarp as any)(warp, id, perspectiveId, cylinderId, placementId),
    );

    if (response.perspectiveWarp && response.tubeWarp && response.documentSize) {
        const transforms = response.placementWarp
            ? [response.perspectiveWarp, response.tubeWarp, response.placementWarp]
            : [response.perspectiveWarp, response.tubeWarp];
        yield* put(
            replaceTransforms({
                id,
                transforms,
                width: response.documentSize.width,
                height: response.documentSize.height,
            }),
        );
    }
}

function* createCylinderWarp({
    payload,
}: PayloadAction<{ id: string; width: number; height: number; warpType: string }>): Generator<any, void, any> {
    const { id, width, height, warpType } = payload;
    if (warpType === CYLINDER_WARP) {
        const cylinder = createCylinderWarpParameters(width, height);
        yield* put(updateCylinder({ id, cylinder }));
    }
}

function* uploadPrecisionWrap({
    payload,
}: PayloadAction<{ id: string; width: number; diameter: number }>): Generator<any, void, any> {
    const { id, width, diameter } = payload;
    if (width > 0 && diameter > 0) {
        yield* call(uploadCylinderWarp, id);
    }
}

function* uploadFromId({ payload }: PayloadAction<string | { id: string }>): Generator<any, void, any> {
    const id = typeof payload === 'string' ? payload : payload.id;
    yield* call(uploadCylinderWarp, id);
}

/** Maps `computeAutoPosition`'s structured failure reasons to a message a designer can act on —
 * these can occur even when `getGarmentWarpAvailability` allowed the action (it checks that a
 * size chart exists at all, not that the specific body-length/width attribute it needs is present
 * within it), so this is the last line of defense against a silent no-op. */
function describeFailureReason(reason: AutoPositionFailureReason): string {
    switch (reason) {
        case 'no-panel-data':
            return 'No decoration-placement data is available for this surface.';
        case 'no-body-width':
            return "The size chart doesn't have a body width measurement for the current size.";
        case 'no-body-length':
            return "The size chart doesn't have a body length measurement for the current size.";
        case 'invalid-body-width':
            return 'The size chart body width measurement is invalid.';
        case 'invalid-body-length':
            return 'The size chart body length measurement is invalid.';
        case 'invalid-markers':
            return 'The garment markers have zero distance on the measurement axis.';
        default:
            return 'Required apparel positioning data is missing.';
    }
}

/** Options shared by every `GARMENT_WARP` placement helper below, so the three signatures that
 * pass it through (`computeAndApplyGarmentPlacement`, `deriveGarmentPlacementInputs`,
 * `resyncGarmentPlacement`) can't drift out of sync with each other. */
type GarmentPlacementSyncOptions = { silent?: boolean };

/**
 * Runs `computeAutoPosition` for a `GARMENT_WARP` document and applies the result — shared by
 * initial entry into the warp mode, explicit "Recompute Placement" clicks, and the background
 * resyncs below. Early validation failures dispatch a user-visible alert to explain missing
 * inputs, *unless* `silent` is set — used by `syncGarmentWarpDocuments` so a decoration-option
 * change that transiently leaves two independently-refetched pieces of state
 * (`surfaces.list`/`decorationPlacement`) out of step with each other for a moment doesn't flash
 * an alert the designer never asked for; the next settle of either one retries automatically.
 *
 * If the background does not yet have calibrated landmarks, this falls back to `defaultMarkers`
 * so the initial auto-placement works right away and can be fine-tuned afterward.
 */
function* computeAndApplyGarmentPlacement(
    id: string,
    garmentPlacement: GarmentPlacementInputType,
    { silent = false }: GarmentPlacementSyncOptions = {},
): Generator<any, void, any> {
    const { backgroundId, panelName, scaleAxis } = garmentPlacement;
    const background = yield* select(getBackgroundById, backgroundId);
    if (!background?.width || !background.height) {
        logError(`Unable to compute garment auto-placement for document ${id}: no scene-defining background`);
        if (!silent) {
            yield* put(garmentPlacementFailed('No background has been uploaded yet.'));
        }
        return;
    }
    const markers = background.garmentMarkers ?? defaultMarkers({ width: background.width, height: background.height });

    const { decorationPlacement, bodyWidthMm, bodyLengthMm } = yield* select(getDecorationSpecs);
    const panel = decorationPlacement?.find((entry) => entry.panelName === panelName) ?? null;

    const result = computeAutoPosition({
        markers,
        panel,
        bodyWidthMm,
        bodyLengthMm,
        scaleAxis,
    });

    if (!result.ok) {
        logError(`Unable to compute garment auto-placement for document ${id}: ${result.reason}`);
        if (!silent) {
            yield* put(garmentPlacementFailed(describeFailureReason(result.reason)));
        }
        return;
    }

    // Convert background pixel coordinates into scene transform space. A no-op in the normal
    // single-background case (where this background is the scene-defining one), but scales
    // appropriately if a secondary background has different native dimensions.
    const sceneSize = yield* select(getSceneSize);
    const ratio = sceneSize.width && background.width ? sceneSize.width / background.width : 1;

    const points = [
        { x: result.x * ratio, y: result.y * ratio },
        { x: (result.x + result.width) * ratio, y: (result.y + result.height) * ratio },
    ];

    if (result.outsideGarmentBounds && !silent) {
        yield* put(garmentPlacementOutsideBounds());
    }

    yield* put(recomputeGarmentPlacement({ id, garmentPlacement, points }));
}

/**
 * Derives a `GARMENT_WARP` document's placement inputs (panel/background to join against) from
 * its *current* surface — shared by entering the mode, `changePageForGarmentWarp`, and
 * `syncGarmentWarpDocuments` below. Dispatches a user-visible alert and returns `undefined` when
 * the document isn't on a recognized surface or no background has been uploaded yet, unless
 * `silent` is set (see `computeAndApplyGarmentPlacement`'s doc comment for why).
 *
 * `scaleAxis` defaults to `DEFAULT_SCALE_AXIS` (there's nothing to preserve yet on first entry),
 * but callers resyncing an *existing* placement pass the document's current `scaleAxis` through —
 * without that, a document ever placed in `'width'` mode would get silently reset to `'height'` on
 * its next surface change or decoration-option-triggered resync, with no UI action responsible.
 */
function* deriveGarmentPlacementInputs(
    id: string,
    { silent = false, scaleAxis = DEFAULT_SCALE_AXIS }: GarmentPlacementSyncOptions & { scaleAxis?: ScaleAxis } = {},
): Generator<any, GarmentPlacementInputType | undefined, any> {
    const doc = yield* select(getDocumentById, id);
    const surface = doc?.page ? yield* select(getSurfaceByPage, doc.page) : undefined;
    const backgroundId = yield* select(getSceneDefiningBackgroundId);

    if (!surface?.name) {
        logError(`Unable to resolve GARMENT_WARP inputs for document ${id}: no surface for page ${doc?.page}`);
        if (!silent) {
            yield* put(garmentPlacementFailed("This document isn't on a recognized surface/page."));
        }
        return undefined;
    }
    if (!backgroundId) {
        logError(`Unable to resolve GARMENT_WARP inputs for document ${id}: no scene-defining background`);
        if (!silent) {
            yield* put(garmentPlacementFailed('No background has been uploaded yet.'));
        }
        return undefined;
    }

    return {
        backgroundId,
        panelName: surface.panelName ?? surface.name,
        scaleAxis,
    };
}

/**
 * Re-derives and reapplies one `GARMENT_WARP` document's placement from its current surface —
 * the common step behind entering the mode, a surface change, and a background data resync.
 *
 * Skips the recompute entirely when the derived `panelName`/`backgroundId`/`scaleAxis` already
 * match the document's stored `garmentPlacement` — i.e. nothing about *this* document's own
 * surface/panel join actually changed. This matters most for `syncGarmentWarpDocuments` (below):
 * without it, every `GARMENT_WARP` document in the scene would get its points unconditionally
 * recomputed and overwritten on *every* surfaces/decorationPlacement refetch, even ones whose own
 * panel never moved — silently discarding any manual point-drag the designer made since the last
 * compute, which is exactly what `recomputeGarmentPlacement`'s own doc comment and
 * `RecomputePlacement`'s "only on explicit user trigger" doc comment say must not happen. A
 * document whose join target *did* change (the actual bug this is fixing) still recomputes, same
 * as `changePageForGarmentWarp`'s already-accepted trade-off of discarding stale points for a
 * surface that no longer matches them.
 */
function* resyncGarmentPlacement(id: string, options: GarmentPlacementSyncOptions = {}): Generator<any, void, any> {
    const doc = yield* select(getDocumentById, id);
    const garmentPlacement = yield* call(deriveGarmentPlacementInputs, id, {
        ...options,
        scaleAxis: doc?.garmentPlacement?.scaleAxis,
    });
    if (!garmentPlacement) {
        return;
    }

    if (isEqual(doc?.garmentPlacement, garmentPlacement)) {
        return;
    }

    yield* call(computeAndApplyGarmentPlacement, id, garmentPlacement, options);
}

export function* enterGarmentWarp({
    payload,
}: PayloadAction<{ id: string; warpType: string }>): Generator<any, void, any> {
    const { id, warpType } = payload;
    if (warpType !== GARMENT_WARP) {
        return;
    }

    yield* call(resyncGarmentPlacement, id);
}

/**
 * Keeps a `GARMENT_WARP` document's placement inputs joined to whichever surface it's currently
 * on. The "Surface" dropdown dispatches `changePage` directly against the documents slice —
 * bypassing `setWarp`/`enterGarmentWarp` entirely — so without this, switching surfaces while
 * already in `GARMENT_WARP` mode left `garmentPlacement.panelName` pointing at the *previous*
 * surface's panel. "Recompute Placement" would then keep recomputing against that stale panel
 * (not the one now shown as selected in the Surface dropdown and Decoration Placement section),
 * surfacing as "No decoration-placement data is available for this surface" whenever the stale
 * panel doesn't happen to also exist under the new surface's product/variables.
 */
export function* changePageForGarmentWarp({
    payload,
}: PayloadAction<{ id: string; page: number }>): Generator<any, void, any> {
    const { id } = payload;
    const doc = yield* select(getDocumentById, id);
    if (doc?.warpType !== GARMENT_WARP) {
        return;
    }

    yield* call(resyncGarmentPlacement, id);
}

/**
 * Resyncs every `GARMENT_WARP` document's placement inputs whenever `surfaces.list` or
 * `decorationPlacement` are refetched — both can change *which panel a page/surface now means*
 * without the document's own `page` ever changing, which `changePageForGarmentWarp` has no way to
 * see. This is exactly what happens when a designer changes a decoration option (`applyVariables`):
 * it kicks off `getSurfaces` (new surfaces/panelNames — see `surfaces/saga.ts`) and
 * `fetchDecorationSpecsAndLinkedAttributes` (new DIS `decorationPlacement` — see
 * `decorationSpecs/saga.ts`) concurrently, neither of which previously resynced an
 * already-placed document — so "Recompute Placement" kept failing against the panel the document
 * was placed on *before* the option change, until something else (re-touching the Surface
 * dropdown, which merely happens to redispatch `changePage` with the same page) incidentally
 * forced a resync.
 *
 * Runs silently (see `computeAndApplyGarmentPlacement`'s doc comment): `surfaces.list` and
 * `decorationPlacement` settle independently, so a resync triggered by whichever lands first may
 * transiently fail to match the other's still-stale data — the next one to settle resyncs again
 * and self-corrects, same as the `cacheKey`/`productModelKey` staleness guards elsewhere in this
 * data flow. Nothing to alert on unless a designer explicitly asks (via "Recompute Placement" or
 * the Surface dropdown) once both have actually settled.
 */
export function* syncGarmentWarpDocuments(): Generator<any, void, any> {
    const documents = yield* select(getAllDocuments);
    const garmentWarpDocumentIds = documents.filter((doc) => doc.warpType === GARMENT_WARP).map((doc) => doc.id);

    yield* all(garmentWarpDocumentIds.map((id) => call(resyncGarmentPlacement, id, { silent: true })));
}

export function* recomputeGarmentPlacementFromRequest({
    payload,
}: PayloadAction<{ id: string }>): Generator<any, void, any> {
    const { id } = payload;
    const doc = yield* select(getDocumentById, id);
    if (!doc?.garmentPlacement) {
        logError(`Unable to recompute garment placement for document ${id}: not in GARMENT_WARP mode`);
        yield* put(
            garmentPlacementFailed('This document is not in garment auto-placement mode — select "Auto-Place" first.'),
        );
        return;
    }

    yield* call(computeAndApplyGarmentPlacement, id, doc.garmentPlacement);
}

export default function* documentsSaga() {
    return yield* all([
        yield* takeEvery(uploadMask, uploadAndAddMask),
        yield* takeEvery(setWarp, createCylinderWarp),
        yield* takeEvery(updateCylinder, uploadFromId),
        yield* takeEvery(toggleFullWrap, uploadFromId),
        yield* takeEvery(rotateCylinder, uploadFromId),
        yield* takeEvery(updatePrecisionWrap, uploadPrecisionWrap),
        yield* takeEvery(uploadTexture, uploadAndAddTexture),
        yield* takeEvery(setWarp, enterGarmentWarp),
        yield* takeEvery(changePage, changePageForGarmentWarp),
        yield* takeEvery(retrieveSurfacesSuccess, syncGarmentWarpDocuments),
        yield* takeEvery(retrieveDecorationSpecsSettled, syncGarmentWarpDocuments),
        yield* takeEvery(requestGarmentPlacementRecompute, recomputeGarmentPlacementFromRequest),
    ]);
}
